/**
 * Hotel Pricing
 *
 * @since 0.1.0
 */

import dayjs from 'dayjs';
import {
	isBot,
	stHotelsTest,
	stHotelsRateEndpoint,
	requestBody,
	fillInMIAHotels,
	restructureRatesResponse,
	mergeResponses,
	clearResponseCache,
	startRequests,
	completeRequests,
	isRequestComplete,
} from './st-hotels-rate-utils';

const $ = window.jQuery;

// Set vars.
const StSharedTAPricing = window.StSharedTAPricing || {};
const tracking = window.STSharedTracking || {};
const config = StSharedTAPricing.config;
const $document = $(document);
const wp = window.wp || {};

/**
 * Build API URL
 *
 * @since 0.1.0
 *
 * @param {array} args URL param.
 * @returns {string} URL.
 */
StSharedTAPricing.buildURL = function (args) {
	const url = new URL(config.apiURL);
	const queryString = url.search;
	const searchParams = new URLSearchParams(queryString);

	if ('undefined' !== args.mcid && undefined !== args.mcid) {
		searchParams.set('mcid', args.mcid);
	}

	for (let key in args) {
		searchParams.set(key, args[key]);
	}

	// change the search property of the main url
	url.search = searchParams.toString();

	// the new url string
	const newUrl = url.toString();

	return newUrl;
};
/**
 * Debug Log
 *
 * @since 0.1.0
 *
 * @param {mixed} pricingData Data to log.
 * @returns {string} string
 */
StSharedTAPricing.debugLog = function (pricingData) {
	if (window.console && config.isDebug) {
		window.console.log(pricingData);
	}
};
/**
 * Sanitize Single Offer.
 * Make sure all data needed available.
 *
 * @since 0.1.0
 *
 * @param {object} offer Offer data.
 * @return {object} Offer data.
 */
StSharedTAPricing.sanitizeOffer = function (offer) {
	const offerData = {
		id: '',
		availability: '',
		displayName: '',
		internalName: '',
		displayPrice: '',
		dailyRate: '',
		link: '',
		logo: '',
	};

	$.extend(offerData, offer);

	return offerData;
};
/**
 * Add Spinner.
 *
 * @since 0.1.0
 *
 * @returns {void}
 */
StSharedTAPricing.addSpinner = function (hotelId) {
	const spinner = $(`[data-hotel=${hotelId}] .loading-spinner--container`);
	spinner.addClass('visible');
	spinner.removeClass('hidden');
};

/**
 * Remove Spinner.
 *
 * @since 0.1.0
 *
 * @param {array}  hotelId    Hotel IDs in comma separated format.
 * @returns {void}
 */
StSharedTAPricing.removeSpinner = function (hotelId) {
	const spinner = $(`[data-hotel=${hotelId}] .loading-spinner--container`);
	spinner.removeClass('visible');
	spinner.on('transitionend webkitTransitionEnd webkitAnimationEnd oAnimationEnd MSAnimationEnd', () => {
		spinner.addClass('hidden');
	});
};

/**
 * Get timout setting for poll
 *
 * @param {int} count number of times polled
 *
 * @returns {void}
 */
StSharedTAPricing.getTimeout = (count) => {
	if (3 >= count) {
		return 500;
	} else if (5 >= count) {
		return 1000;
	} else if (7 >= count) {
		return 2000;
	} else if (8 === count) {
		return 3000;
	} else if (9 === count) {
		return 5000;
	}

	return 10000;
};

/**
 * Get Single Hotel Offers Data From API
 *
 * This will add Offers data in element
 * ".st-ta-hotel-offers[data-hotel={Hotel ID}]"
 * using JS template set.
 *
 * @since 0.1.0
 *
 * @param {map}  hotelMetaData      map of TA Hotel IDs to gimmonixHotelIds and city address
 * @param {string} pricingForm      Pricing form element. e.g "#pricing-form".
 *
 * @returns {mixed} on success pricing offers data to getOffers
 */
StSharedTAPricing.getOffers = async function (hotelMetaData, pricingForm) {
	if (isBot()) {
		return;
	}

	const hotelIDs = Object.keys(hotelMetaData || {});
	if (!hotelIDs || !pricingForm) {
		return;
	}

	const trackingArgs = tracking.addApiArgs();
	const self = this;
	const $form = $(pricingForm);
	let checkinDate = '';
	let checkoutDate = '';

	const dateRange = $form.find('input[name="checkin-out"]').val();

	const dates = dateRange.split(' to ');

	if (dates && dates[0]) {
		checkinDate = dayjs(dates[0]);
	}
	if (dates && dates[1]) {
		checkoutDate = dayjs(dates[1]);
	}

	if (!checkinDate || !checkoutDate) {
		return;
	}

	if ('undefined' !== window.JetsetterTracking && undefined !== window.JetsetterTracking) {
		const tracking = window.JetsetterTracking.addApiArgs();
		trackingArgs.mcid = tracking.mcid;
	}

	const gimmonixHotelIds = [];
	const gimmonixIdToHotelMap = {};

	if (stHotelsTest()) {
		hotelIDs.forEach((taId) => {
			const gimmonixHotelId = hotelMetaData[taId].gimmonixHotel;
			if (gimmonixHotelId) {
				gimmonixHotelIds.push(gimmonixHotelId);
				gimmonixIdToHotelMap[gimmonixHotelId] = {
					...hotelMetaData[taId],
					...{
						taId,
						cityAddress: hotelMetaData[taId].hotelCityAddress,
					},
				};
			}
		});
	}

	const fetchArgs = {
		taHotelIds: hotelIDs,
		gimmonixHotelIds,
		hotelMetaData,
		checkinDate,
		checkoutDate,
		rooms: $form.find('select[name=rooms]').val() || 1,
		adults: $form.find('select[name="guests"]').val() || 1,
		trackingArgs,
	};

	// Room total cannot be more than adults total.
	if (fetchArgs.rooms > fetchArgs.adults) {
		fetchArgs.rooms = fetchArgs.adults;
	}

	hotelIDs.forEach((id) => self.addSpinner(id));

	clearResponseCache(hotelIDs);

	// Start request.
	const fetches = [self.fetchTA];

	if (stHotelsTest()) {
		fetches.push(self.fetchStHotels);
	}

	fetches.forEach((doFetch) => {
		doFetch({
			fetchArgs,
			gimmonixIdToHotelMap,
			success: (response) => {
				if (Array.isArray(response.results)) {
					// Add to DOM.
					self.addOffers(mergeResponses(response));
				}
			},
			fail: () => {
				hotelIDs.forEach((id) => StSharedTAPricing.removeSpinner(id));
			},
			error: () => {
				hotelIDs.forEach((id) => StSharedTAPricing.removeSpinner(id));
			},
		});
	});
};

StSharedTAPricing.fetchTA = ({ fetchArgs, success, fail, error, newRequest = true, requestCount = 0 }) => {
	const { taHotelIds, checkinDate, checkoutDate, rooms, adults, trackingArgs } = fetchArgs;

	if (taHotelIds.length === 0) {
		return;
	}

	const taPricingArgs = {
		hotel: taHotelIds.join(','),
		checkin: checkinDate.format('YYYY-MM-DD'),
		nights: checkoutDate.diff(checkinDate, 'days'),
		rooms,
		adults,
		new_request: newRequest ? 'true' : 'false',
		...trackingArgs,
	};

	startRequests(taHotelIds);

	$.ajax({
		url: StSharedTAPricing.buildURL(taPricingArgs),
		type: 'get',
		dataType: 'json',
		success: (response) => {
			fillInMIAHotels(taHotelIds, response);
			completeRequests(taHotelIds);

			// Refetch get data if not complete.
			if (!response.status.isComplete) {
				setTimeout(() => {
					StSharedTAPricing.fetchTA({
						fetchArgs,
						success,
						fail,
						error,
						newRequest: false,
						requestCount: requestCount + 1,
					});
				}, StSharedTAPricing.getTimeout(requestCount));
			}

			success(response);
		},
		fail: (...args) => {
			completeRequests(taHotelIds);
			fail(...args);
		},
		error: (...args) => {
			completeRequests(taHotelIds);
			error(...args);
		},
	});
};

StSharedTAPricing.fetchStHotels = ({ fetchArgs, gimmonixIdToHotelMap, success, fail, error }) => {
	const { gimmonixHotelIds, taHotelIds } = fetchArgs;

	if (gimmonixHotelIds.length === 0) {
		return;
	}

	startRequests(taHotelIds, true);

	$.ajax({
		url: stHotelsRateEndpoint(),
		type: 'post',
		dataType: 'json',
		data: JSON.stringify(requestBody(fetchArgs)),
		success: (response) => {
			const restructuredResponse = restructureRatesResponse(response, fetchArgs, gimmonixIdToHotelMap);
			completeRequests(taHotelIds, true);
			success(restructuredResponse);
		},
		fail: (...args) => {
			completeRequests(taHotelIds, true);
			fail(...args);
		},
		error: (...args) => {
			completeRequests(taHotelIds, true);
			error(...args);
		},
	});
};

$document.on('change', '.cta-widget-dates .destination-datepick', (e) => {
	const target = $(e.currentTarget) || $(e.target);
	const el = target.parents('.call-to-action[data-hotel]');
	const form = el.find('.form-ta-meta-product-date-select').get(0);
	const hotelId = el.attr('data-hotel');
	const hotelMetaData = { [hotelId]: $(el).data() };

	StSharedTAPricing.getOffers(hotelMetaData, form);
});

/**
 * TripAdvisor Pricing Init
 *
 * @returns {void}
 */
const init = () => {
	if (!document.body.classList.contains('single-post')) {
		return;
	}

	/**
	 * Set Method to Add Offer.
	 *
	 * Note: used by smartertravel.com with the `Hotel Call to Action - Property` shortcode
	 *
	 * @param {array} resultData Offer results.
	 * @returns {void}
	 */
	StSharedTAPricing.addOffers = (resultData) => {
		const templateOfferCTAButton = wp.template('js-ta-pricing-cta-button');
		const templateOfferCTAItem = wp.template('js-ta-pricing-cta-list-item');

		const results = resultData.results;
		const unsupportedHotelIds = resultData.unsupportedHotelIds || [];
		const invalidHotelIds = resultData.invalidHotelIds || [];

		[...unsupportedHotelIds, ...invalidHotelIds].forEach((id) => {
			StSharedTAPricing.removeSpinner(id);

			const $offers = $(`.call-to-action[data-hotel=${id}]`);
			$offers.find('.ta-meta-button-text').html(templateOfferCTAButton('invalidId'));
		});

		// Loop results.
		$.each(results, (index, value) => {
			if (undefined === value.id) {
				return false;
			}

			const offers = value.hacOffers.offers || [];

			const $offers = $(`.call-to-action[data-hotel=${value.id}]`);
			const gimmonixId = $offers.data('gimmonix-hotel');
			const isComplete = isRequestComplete(value.id, gimmonixId);
			const count = Object.keys(offers).length;

			// Add count.
			$offers.data('count', count).attr('data-count', count);

			// Reset list.
			$offers.find('.ta-meta-button-text').html('');
			$offers.find('.ta-menu-list').html('');

			if (isComplete) {
				StSharedTAPricing.removeSpinner(value.id);
			}

			// Loop offers.
			$.each(offers, (offerIndex, offer) => {
				const i = offerIndex + 1;
				const offerData = StSharedTAPricing.sanitizeOffer(offer);

				offerData.index = i;

				if (offerData.displayPrice) {
					StSharedTAPricing.removeSpinner(value.id);
				}

				if (1 === i) {
					$offers.find('.ta-meta-button-text').html(templateOfferCTAButton(offerData));
				}

				$offers.find('.ta-menu-list').append(templateOfferCTAItem(offerData));
			});

			return false;
		});
	};

	/**
	 * TripAdvisorPricing Action
	 *
	 * @returns {void}
	 */
	const TripAdvisorPricingAction = () => {
		$document.ready(() => {
			// Only on singular post.
			if (!document.body.classList.contains('single-post')) {
				return;
			}

			const ctaItems = $document.find('.call-to-action[data-hotel]');

			if (ctaItems) {
				ctaItems.each(function () {
					const hotelMetaData = {};

					$(this).each(function () {
						const hotelId = $(this).data('hotel');
						hotelMetaData[hotelId] = $(this).data();
					});

					const pricingForm = '.form-ta-meta-product-date-select';

					if (0 !== Object.keys(hotelMetaData).length) {
						// Initial Load.
						StSharedTAPricing.getOffers(hotelMetaData, pricingForm);
					}
				});
			}
		});
	};

	/**
	 * CTA Modal Submit function
	 *
	 * @returns {void}
	 */
	const pricingCTASubmit = () => {
		const formContainers = document.querySelectorAll('.call-to-action[data-hotel]');

		if (formContainers) {
			formContainers.forEach((el) => {
				const form = el.querySelector('.form-ta-meta-product-date-select');
				const formSubmit = form.querySelector('.ta-meta-product-submit');
				const hotelId = el.getAttribute('data-hotel');
				const hotelMetaData = { [hotelId]: $(el).data() };

				/**
				 * Submit Pricing Action
				 *
				 * @param {object} event event object
				 * @returns {void}
				 */
				const submitCTAPricing = (event) => {
					event.preventDefault();
					const buttonLink = el.querySelector('.ta-meta-button-link');
					const datesModal = document.querySelector('.modal-open');
					const fadeEl = Array.from(datesModal.querySelectorAll('.fade'));

					buttonLink.innerHTML = 'Loading Rates...';
					StSharedTAPricing.getOffers(hotelMetaData, form);

					if (!datesModal) {
						// bail if correct container does not exist
						return;
					}

					datesModal.classList.toggle('modal-open');
					document.body.classList.toggle('-js-modal-open');

					if (fadeEl) {
						fadeEl.forEach((element) => {
							setTimeout(() => {
								element.classList.toggle('in');
							}, 300);
						});
					}
				};

				formSubmit.addEventListener('click', submitCTAPricing);
			});
		}
	};

	TripAdvisorPricingAction();
	pricingCTASubmit();
};

export default { init };
